/**
 * EmojiPicker — popover simple con emojis curados (sin dependencia externa).
 *
 * Botón smiley que abre un grid de ~30 emojis de salud/comunes; al elegir uno
 * llama onSelect(emoji) y cierra. Cierra con Escape o clic fuera.
 */
import { useEffect, useRef, useState } from 'react';
import { Smile } from 'lucide-react';
import { EMOJIS_CURADOS } from './tiposCampana';
import styles from './EmojiPicker.module.css';

interface EmojiPickerProps {
  onSelect: (emoji: string) => void;
  /** Etiqueta accesible del botón, ej. "Insertar emoji en el título". */
  label: string;
}

export function EmojiPicker({ onSelect, label }: EmojiPickerProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.trigger}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="true"
        title={label}
        onClick={() => setOpen((v) => !v)}
      >
        <Smile size={16} aria-hidden="true" />
      </button>

      {open && (
        <div className={styles.popover} role="menu" aria-label="Emojis disponibles">
          {EMOJIS_CURADOS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              role="menuitem"
              className={styles.emojiBtn}
              onClick={() => {
                onSelect(emoji);
                setOpen(false);
              }}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
